import React from 'react'
import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { MultiAPILink } from '@habx/apollo-multi-endpoint-link'
import { RestLink } from 'apollo-link-rest'
import { createUploadLink } from 'apollo-upload-client'
import { useCookies } from 'react-cookie'

const useApollo = () => {
    const [cookies] = useCookies([''])
    // @ts-expect-error TODO: fix cookie types
    const { account_id, authorization } = cookies

    const authConfig = React.useMemo(
        () => ({
            headers: {
                authorization: `Bearer ${authorization}`,
                account_id: account_id || 'undefined',
            },
        }),
        [account_id, authorization],
    )

    const errorLink = React.useMemo(
        () =>
            onError(({ graphQLErrors }) => {
                graphQLErrors?.forEach(({ extensions }: any) => {
                    if (
                        extensions?.exception?.status === 401 ||
                        extensions?.response?.statusCode === 401 ||
                        extensions?.code === 'UNAUTHENTICATED'
                    ) {
                        // logout
                    }
                })
            }),
        [],
    )

    const mLink = React.useMemo(
        () =>
            new MultiAPILink({
                endpoints: {
                    ai: `${import.meta.env.REACT_APP_ACCOUNT_SERVICES_URL}`,
                },
                createHttpLink: () => createHttpLink({}),
                getContext: () => authConfig,
            }),
        [authConfig],
    )

    const restLink = React.useMemo(
        () =>
            new RestLink({
                endpoints: {
                    ai: `${import.meta.env.REACT_APP_ACCOUNT_SERVICES_URL}`,
                },
                ...authConfig,
            }),
        [authConfig],
    )

    const uploadLink = React.useMemo(
        () =>
            createUploadLink({
                uri: `${import.meta.env.REACT_APP_SERVICES_URL}/graphql`,
                ...authConfig,
            }),
        [authConfig],
    )

    const apolloLink = React.useMemo(
        () => ApolloLink.from([errorLink, ApolloLink.from([restLink, mLink, uploadLink])]),
        [errorLink, restLink, mLink, uploadLink],
    )

    const apollo = React.useMemo(
        () =>
            new ApolloClient({
                link: apolloLink,
                cache: new InMemoryCache(),
                connectToDevTools: true,
                defaultOptions: {
                    watchQuery: {
                        fetchPolicy: 'cache-and-network',
                    },
                    query: {
                        fetchPolicy: 'cache-first',
                        errorPolicy: 'all',
                    },
                    mutate: {
                        errorPolicy: 'all',
                    },
                },
            }),
        [apolloLink],
    )

    return apollo
}

export default useApollo
